/**
 * Fxxl Newsletter Popup
 *
 * Filename: newsletter_popup.js
 *
 * @category  Fahrradxxl
 * @package   Module
 * @author    Janik Kemnade <j.kemnade@fahrrad-xxl.de>
 * @copyright 2024 Fahrrad-xxl.de
 */


import FxxlCore from '@fxxl/core';
import FxxlError from '@fxxl/error';
import FxxlGTM from '@fxxl/gtm';
import FxxlShopSuggestion from '@fxxl/shop_suggestion';
import FxxlPageContext from '@fxxl/page_context';


/* Window Variables
 * ------------------------------------------------------------------------- */
(() => {
    const config = FxxlCore.windowVariable('fxxlNewsletterPopupConfig');

    const parseValue = (value, dataType) => {
        if (dataType === 'boolean') {
            return value === 'true';
        }
        if (dataType === 'number')  {
            return value != null ? Number(value) : null;
        }
        return value;
    };

    config.desktop                      = parseValue(config?.desktop,                   'boolean');
    config.mobile                       = parseValue(config?.mobile,                    'boolean');
    config.showDelay                    = parseValue(config?.showDelay,                 'number');
    config.autoHideDelay                = parseValue(config?.autoHideDelay,             'number');
    config.autoHideMaxCount             = parseValue(config?.autoHideMaxCount,          'number');
    config.mobileSiteChangeMaxCount     = parseValue(config?.mobileSiteChangeMaxCount,  'number');
    config.desktopSiteChangeMaxCount    = parseValue(config?.desktopSiteChangeMaxCount, 'number');

    // Set
    window.fxxlNewsletterPopupConfig = config;
})();


// FxxlNewsletterPopup aims to be a Singelton
export default class FxxlNewsletterPopup {
    /* Static Variables - GLOBALS
     * --------------------------------------------------------------------- */
    static ERROR_MESSAGES_PREFIX = 'FxxlNewsletterPopup';
    static get ERROR_MESSAGE() {
        return {
            INSTANCE_EXISTS:        `${FxxlNewsletterPopup.ERROR_MESSAGES_PREFIX}: Call: An Instance exists`,
            PARAM_NOT_OBJECT:       `${FxxlNewsletterPopup.ERROR_MESSAGES_PREFIX}: Call: Param not object`,
            ELEMENTS_NOT_EXISTING:  `${FxxlNewsletterPopup.ERROR_MESSAGES_PREFIX}: Collect: Not all elements existing`
        };
    }

    /* Static Variables - CONFIG
     * --------------------------------------------------------------------- */
    static get CONFIG() {
        const DISPLAYBLOCK_CONFIG_VARS  = FxxlCore.windowVariable('fxxlNewsletterPopupConfig');
        const storageKeyFirstAction     = FxxlCore.getConfig()?.storageKeys?.firstAction;
        const storageKeyUtm             = FxxlCore.getConfig()?.storageKeys?.utmSourceNewsletter;
        const storageKeyNewsletterPopup = FxxlCore.getConfig()?.storageKeys?.newsletterPopup;

        return {
            DISPLAYBLOCK: DISPLAYBLOCK_CONFIG_VARS,
            STORAGE_UTM: FxxlCore.getStorage(storageKeyUtm),
            STORAGE_POPUP: FxxlCore.getStorage(storageKeyNewsletterPopup),
            COOKIE_WAIT: {
                hasFirstAction: !!FxxlCore.getStorage(storageKeyFirstAction),
                repeatTimeMilliseconds:    1000, // 1 Sec
                timeoutTimeMilliseconds: 120000, // 2 Min
            }
        };
    }


    /* Static Class Initialization - Get Instance
     * --------------------------------------------------------------------- */
    /**
     * Safely Initializes FxxlNewsletterPopup.
     * Because conditions need to be met, this serves as the function to initialize.
     * PLS ONLY USE THIS FUNCTION! -> Ich finde dich 🫵!!!
     *
     * Note:
     *  Unfortunately there is no JS way to force a function to be the only way to call something else.
     *  There is new Error().stack, but this is not implemented among browsers.
     *  This serves as the "best there is" approach.
     *
     * @param {HTMLElement} element
     */
    static async getInstance(element) {
        if (typeof element !== 'object' || ! element instanceof HTMLElement) {
            new FxxlError(FxxlNewsletterPopup.ERROR_MESSAGE.PARAM_NOT_OBJECT);
            return;
        }

        // Gathering Configs
        const CONFIG = FxxlNewsletterPopup.CONFIG;

        // Condition Vars
        const isMobile = !isLargeViewport();
        const isDesktop = isLargeViewport();

        const isSiteNewsletter =
            site_type === 'newsletter'
            && ( site_name === 'emailsent' || site_name === 'activate' || site_name === 'confirm' );

        const isPopupDeactivated                = CONFIG?.STORAGE_POPUP?.deactivated || false;
        const urlContainsUTMSourceNewsletter    = FxxlPageContext.urlContainsUTMSourceNewsletter();
        const existsUtmNewsletterSession        = CONFIG?.STORAGE_UTM?.utmSource === 'newsletter';
        const reachedAutoHideMaxCount           = CONFIG?.STORAGE_POPUP?.autoHideCount >= CONFIG?.DISPLAYBLOCK?.autoHideMaxCount;
        const mobileReachedSiteChangeMaxCount   = CONFIG?.STORAGE_POPUP?.siteChangeCount
                                                    >= CONFIG?.DISPLAYBLOCK?.mobileSiteChangeMaxCount
                                                    && isMobile;
        const desktopReachedSiteChangeMaxCount  = CONFIG?.STORAGE_POPUP?.siteChangeCount
                                                    >= CONFIG?.DISPLAYBLOCK?.desktopSiteChangeMaxCount
                                                    && isDesktop;

        // Checks
        if (
            isSiteNewsletter
            || isPopupDeactivated
            || urlContainsUTMSourceNewsletter  || existsUtmNewsletterSession
            || mobileReachedSiteChangeMaxCount || desktopReachedSiteChangeMaxCount
            || reachedAutoHideMaxCount
            || isMobile && CONFIG?.DISPLAYBLOCK?.mobile === false
            || isDesktop && CONFIG?.DISPLAYBLOCK?.desktop === false
        ) {
            delete window.fxxlNewsletterPopupConfig;
            element.remove();

            if (
                isSiteNewsletter
                || urlContainsUTMSourceNewsletter  || existsUtmNewsletterSession
                || mobileReachedSiteChangeMaxCount || desktopReachedSiteChangeMaxCount
                || reachedAutoHideMaxCount
            ) {
                FxxlNewsletterPopup.storageSetStatusDeactivated(true);
            }
        } else {
            FxxlNewsletterPopup.waitForMandatoryCookie(
                CONFIG?.COOKIE_WAIT?.repeatTimeMilliseconds,
                CONFIG?.COOKIE_WAIT?.timeoutTimeMilliseconds,
                CONFIG?.COOKIE_WAIT?.hasFirstAction
            ).then(() => {
                new FxxlNewsletterPopup(element);
            })
             .catch(error => {});
        }
    }


    /* Static Methods - Handle Mandatory Cookie Set
     * --------------------------------------------------------------------- */
    /**
     * This Function checks in a given interval for the "fxxl_erforderliche_cookies" Cookie.
     * If the Cookie was found resolve, otherwise wait until a timeout rejects the Promise.
     *
     * Acts mainly as the waiting instance for the cookie banner.
     *
     * @param {number} repeatTime
     * @param {number} timeoutTime
     * @param {boolean} hasFirstAction
     * @returns {Promise<>}
     */
    static waitForMandatoryCookie(repeatTime = 1000, timeoutTime = 20000, hasFirstAction = false) {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            const isMobile  = !isLargeViewport();
            // Check Definition
            const checkForMandatoryCookie = () => {
                // Conditions
                const isSetMandatoryCookie        = FxxlCore.getStorage(FxxlCore.getConfig()?.storageKeys?.cmt);
                const isSetSelectedShopSuggestion = FxxlShopSuggestion.isSetStorageSelectedSuggestedCountry();
                const reachedOverallTimeout       = Date.now() - startTime >= timeoutTime;

                // Check Resolve
                if (isSetMandatoryCookie && isSetSelectedShopSuggestion) {
                    return resolve();
                }

                // Check Overall Timeout
                // On Mobile: Should isSetMandatoryCookie, after a first User Action ( Meant "Second Page Visit" ),
                //      for some reason, not be set, a timeout needs to happen,
                //      otherwise Code would run infinitely.
                // On Desktop: Can be ignored, because the first User Action is immediately set
                //      ( Currently there is no way to check for an active second Page Visit
                //      pls, correct if there is. )
                if (isMobile && hasFirstAction && !isSetMandatoryCookie && reachedOverallTimeout) {
                    return reject();
                }

                // Next Check in Interval
                setTimeout(checkForMandatoryCookie, repeatTime);
            };

            // Do da Call
            checkForMandatoryCookie();
        });
    }



    /* Static Methods - Storage - Get / Set Deactivated
     * --------------------------------------------------------------------- */
    /**
     * LocalStorage: Get Deactivated
     *
     * @returns {boolean}
     */
    static storageGetStatusDeactivated() {
        const storageKey = FxxlCore.getConfig()?.storageKeys?.newsletterPopup;

        return FxxlCore.getStorage(storageKey)?.deactivated || false;
    }

    /**
     * LocalStorage: Set Deactivated
     *
     * @param {boolean} status
     */
    static storageSetStatusDeactivated(status = false) {
        const storageKey = FxxlCore.getConfig()?.storageKeys?.newsletterPopup;
        const data = FxxlCore.getStorage(storageKey) || {};
        data.deactivated = status;

        FxxlCore.setStorage(storageKey, data);
    }


    /* Constructor
     * --------------------------------------------------------------------- */
    constructor(container) {
        // Exit Criteria: Only one Instance acceptable
        if (FxxlNewsletterPopup._instance) {
            new FxxlError(FxxlNewsletterPopup.ERROR_MESSAGE.INSTANCE_EXISTS);
            return;
        }

        // Bind: Instance
        FxxlNewsletterPopup._instance = this;

        // Get: Config
        this.CONFIG = FxxlNewsletterPopup.CONFIG;

        // Init: Presence & Config Variables
        this.popupShowDelay = ( this.CONFIG?.DISPLAYBLOCK?.showDelay * 1000 ) ?? 6000; // ms
        this.autoHideDelay  = ( this.CONFIG?.DISPLAYBLOCK?.autoHideDelay * 1000 ) ?? 15000; // ms

        // Init: Site Information
        this.isMobile  = !isLargeViewport();
        this.isDesktop = isLargeViewport();

        // Init: Base State Variables
        this.DOMElementsGathered = true;
        this.isPopupOpen = false;

        // Get Container Property Values
        this.propertyValues = getComputedStyle(container);
        this.propertyNameAnimationDurationTime  = '--newsletter_popup--animation-duration-time';
        this.propertyValueAnimationDurationTime = parseFloat(
            this.propertyValues.getPropertyValue(this.propertyNameAnimationDurationTime)
        );

        // Init: Base Functionality Variables
        this.showHideAnimationDurationTime =
            (this.propertyValueAnimationDurationTime) ? ( this.propertyValueAnimationDurationTime * 1000 ) : 300;
        this.firstShowDelay = this.popupShowDelay;
        this.autoHideStop   = this.isMobile; // IF Desktop = Don't Stop ( false ) / IF Mobile = Stop ( true )
        this.autoHideStop   = ( this.autoHideDelay === 0 ) ? true : this.autoHideStop;

        // Init & Declare: Timout Func
        this.funcAutoHideTimeout = null;

        // Init & Declare: Base Element Variables
        this.container = container;
        this.closeButton = null;
        this.overlay = null;
        this.form = null;
        this.formInputEmail = null;
        this.formInputAgreementText = null;
        this.formSubmit = null;

        // Init: Base Element Class Variables
        // Class Names
        this.elementPrefix                  = 'fxxl-newsletter-popup';
        this.showClassName                  = `${this.elementPrefix}--show`;
        this.overlayClassName               = `${this.elementPrefix}__overlay`;
        this.showOverlayClassName           = `${this.elementPrefix}__overlay--show`;
        this.closeButtonClassName           = `${this.elementPrefix}__button--close`;
        this.formClassName                  = `${this.elementPrefix}__form`;
        this.formSubmitClassName            = `${this.elementPrefix}__submit`;
        this.formInputEmailClassName        = `${this.elementPrefix}__input_email`;
        this.formIInputAgreementClassName   = `${this.elementPrefix}__agreement_text`;

        // Selectors
        this.showSelector                   = `.${this.showClassName}`;
        this.closeButtonSelector            = `.${this.closeButtonClassName}`;
        this.formSelector                   = `.${this.formClassName}`;
        this.formInputEmailSelector         = `.${this.formInputEmailClassName} input`;
        this.formInputAgreementSelector     = `.${this.formIInputAgreementClassName} input`;
        this.formSubmitSelector             = `.${this.formSubmitClassName}`;



        // Init
        this.getDOMElements();
        if (this.DOMElementsGathered) {
            this.setPopupMaxHeight();
            this.events();
            this.firstShow();
        } else {
            new FxxlError(FxxlNewsletterPopup.ERROR_MESSAGE.ELEMENTS_NOT_EXISTING);
        }
    }


    /* Private Methods - Get DOMElements
     * ------------------------------------------------------------------------ */
    getDOMElements() {
        // Overlay
        this.overlay = document.querySelector(`.${this.overlayClassName}`);

        // Close Button
        this.closeButton = this.container?.querySelector(this.closeButtonSelector);

        // Form
        this.form                   = this.container?.querySelector(this.formSelector);
        this.formInputEmail         = this.form?.querySelector(this.formInputEmailSelector);
        this.formInputAgreementText = this.form?.querySelector(this.formInputAgreementSelector);
        this.formSubmit             = this.form?.querySelector(this.formSubmitSelector);

        if (
            !this.container
            || !this.closeButton
            || !this.form || !this.formInputEmail || !this.formSubmit
            || !this.formInputAgreementText
        ) {
            this.DOMElementsGathered = false;
        }
    }


    /* Private Methods - Events
     * --------------------------------------------------------------------- */
    events() {
        // Close Button -> Click
        this.closeButton?.addEventListener('click', this.eventClickCloseButton.bind(this));

        // Container -> Mouse Enter
        this.container?.addEventListener('mouseenter', this.eventMouseEnterContainerStopAutoHide.bind(this));

        // Container -> Mouse Leave
        this.container?.addEventListener('mouseleave', this.eventMouseLeaveContainerRestartAutoHide.bind(this));

        // Container -> Form -> Submit
        this.formSubmit?.addEventListener('click', this.eventSubmit.bind(this));

        if (this.isMobile) {

            // Overlay -> Click
            this.overlay?.addEventListener('click', this.eventClickOverlay.bind(this));
        }
    }


    /* Private Methods - Events -> Close Button
     * ----------------------------------------------------------- */
    /**
     * Event: Mouseenter -> Popup
     * Handle moving the mouse into the Popup Frame
     */
    eventClickCloseButton() {
        this.hidePopup();
        FxxlNewsletterPopup.storageSetStatusDeactivated(true);
    }


    /* Private Methods - Events -> Popup -> Auto Hide
     * ----------------------------------------------------------- */
    /**
     * Event: Mouseenter -> Popup
     * Handle moving the mouse into the Popup Frame
     */
    eventMouseEnterContainerStopAutoHide() {
        if (this.isDesktop) {
            this.autoHideStop = true;
            clearTimeout(this.funcAutoHideTimeout);
        }
    }

    /**
     * Event: Mouseleave -> Popup
     * Handle moving the mouse away from the Popup Frame
     */
    eventMouseLeaveContainerRestartAutoHide() {
        if (this.isDesktop) {
            this.autoHideStop = false;

            if (this.autoHideDelay !== 0) {
                this.autoHidePopup();
            }
        }
    }


    /* Private Methods - Events -> Overlay -> Hide
     * ----------------------------------------------------------- */
    /**
     * This only exists for Mobile
     *
     * Event: Click
     * Handle clicking the Popup Overlay
     */
    eventClickOverlay() {
        if (this.isMobile && this.isPopupOpen) {
            this.autoHideStop = true;
            this.hidePopup();
        }
    }


    /* Private Methods - Events -> Popup -> Form -> Submit
     * ----------------------------------------------------------- */
    /**
     * Event: Submit
     * Handle Submitting Popup
     */
    eventSubmit(event) {
        event.preventDefault();
        if (this.formValidation(this.form) === true) {
            FxxlCore.eventEmitter('submit').emit(null, this.form);
            FxxlGTM.sendEventFxxlNewsletterPopupSubmit(
                this.formInputEmail?.value,
                FxxlCore.decodeHtmlEntities(this.formInputAgreementText?.value)
            );
        }
    }


    /* Private Methods - Handle Show / Hide
     * --------------------------------------------------------------------- */
    /**
     * Main Function for showing the Popup
     */
    showPopup() {
        this.popupAnimateFlyIn(() => {
            this.isPopupOpen = true;
            FxxlGTM.sendEventFxxlNewsletterPopupShow();
        });
    }

    /**
     * Main Function for hiding the popup
     */
    hidePopup() {
        this.popupAnimateFlyOut(() => {
            this.isPopupOpen = false;
            FxxlGTM.sendEventFxxlNewsletterPopupHide();
        });
    }

    /**
     * Show the Popup for the first time
     */
    firstShow() {
        setTimeout(() => {
            this.showPopup();
            this.autoHidePopup();
            this.storageIncreaseSiteChangeCount();
        }, this.firstShowDelay);
    }

    /**
     * Hide the Popup after a given time
     */
    autoHidePopup() {
        this.funcAutoHideTimeout = setTimeout(() => {
            if (!this.autoHideStop) {
                this.hidePopup();
                this.storageIncreaseAutoHideCount();
            }
        }, this.autoHideDelay);
    }


    /* Private Methods - Helper - Handle Show / Hide Animation
     * --------------------------------------------------------------------- */
    /* Fly Popup in  */
    popupAnimateFlyIn(callback = null) {
        this.container.style.opacity = 0;
        this.container.style.visibility = 'hidden';

        if (this.isDesktop) {
            this.container.style.display = 'flex';
            this.container.style.left    = '0px';
            this.container.style.bottom  = '30px';
        }

        if (this.isMobile) {
            this.overlay.style.display   = 'block';
            this.container.style.display = 'block';
            this.container.style.bottom  = '-20px';
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.container.style.opacity = 1;
                this.container.style.visibility = 'visible';

                if (this.isDesktop) {
                    this.container.style.left = '20px';
                }

                if (this.isMobile) {
                    this.overlay.classList.add(this.showOverlayClassName);
                    this.container.style.bottom = '0px';
                }

                this.container.classList.add(this.showClassName);

                typeof callback == 'function' && callback();
            });
        });
    }

    /* Fly Popup out  */
    popupAnimateFlyOut(callback = null) {
        this.container.style.opacity = 0;
        this.container.style.visibility = 'hidden';

        if (this.isDesktop) {
            this.container.style.left    = '0px';
            this.container.style.bottom  = '30px';
        }

        if (this.isMobile) {
            this.overlay.style.opacity = 0;
            this.overlay.style.visibility = 'hidden';
            this.container.style.bottom  = '-20px';
        }

        setTimeout(() => {
            this.container.style.display = 'none';

            if (this.isMobile) {
                this.overlay.classList.remove(this.showOverlayClassName);
                this.overlay.style.display = 'none';
            }

            this.container.classList.remove(this.showClassName);

            typeof callback == 'function' && callback();
        }, ( this.showHideAnimationDurationTime + 1 ));
    }


    /* Private Methods - Helper - Form Submitting
     * --------------------------------------------------------------------- */
    /**
     * Newsletter Form Validation
     *
     * @param {HTMLElement} form
     * @returns {boolean}
     */
    formValidation(form) {
        FxxlCore.eventEmitter(FxxlCore.getConfig()?.eventNames?.formValidation).emit(null, form);
        return form?.fxxlValidation?.valid ?? false;
    }


    /* Private Methods - Helper - Storage
     * --------------------------------------------------------------------- */
    /**
     * LocalStorage: Increase Value of autoHideCount by 1
     */
    storageIncreaseAutoHideCount() {
        const storageKey = FxxlCore.getConfig()?.storageKeys?.newsletterPopup;
        const data = FxxlCore.getStorage(storageKey) || {};

        data.autoHideCount = ( data.autoHideCount ?? 0 ) + 1;
        FxxlCore.setStorage(storageKey, data);
    }

    /**
     * LocalStorage: Increase Value of autoHideCount by 1
     *
     * Note:
     *  This count does not react to reloads, or url param changes
     *  This Counter starts at 0 and increases by 1 per site change
     */
    storageIncreaseSiteChangeCount() {
        const storageKey = FxxlCore.getConfig()?.storageKeys?.newsletterPopup;
        const data = FxxlCore.getStorage(storageKey) || {};

        data.siteChangeCount = (data?.siteChangeCount === undefined) ? 0 : data.siteChangeCount + 1;

        FxxlCore.setStorage(storageKey, data);
    }


    /* Private Methods - Helper - Popup Max Height
     * --------------------------------------------------------------------- */
    setPopupMaxHeight() {
        if (this.isMobile) {
            return;
        }

        const cssWindowHeight = '100vh';
        const spaceBetweenPopupAndElements = 30;
        const headerSelector = 'header.fxxl-base-header';
        const headerHeight   = document.querySelector(headerSelector)?.offsetHeight || 100;
        const newMaxHeight   = `calc(${cssWindowHeight} - ${headerHeight}px - ${2 * spaceBetweenPopupAndElements}px)`;

        this.container.style.maxHeight = newMaxHeight;
    }

}

